// usePasswordReveal — show/hide state + the eye button for password inputs
// (v3 APPassword parity; extracted from pages/auth/PasswordField on its
// second consumer, APL-12040). The toggle is a SLOT, not a wrapper: render
// it as a sibling of the control inside the floating wrapper — wrapping the
// control in an extra div would make the div the `peer` and break the
// floating label's :placeholder-shown tracking (label sits over the value).
import { EyeClosedLinear, EyeLinear } from '@assessprep/icons/solar';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

export interface UsePasswordRevealOptions {
  /** Disable the toggle (mirror the control's disabled, e.g. while saving). */
  disabled?: boolean;
}

export interface UsePasswordRevealReturn {
  /** Feed to the control's `type`. */
  type: 'password' | 'text';
  /** Absolutely-positioned eye button — render as a sibling of the control. */
  toggle: ReactNode;
  /** Re-mask. Call from permanently-mounted consumers (e.g. a dialog's
   *  open-effect) — otherwise a reveal leaks into the next open. Consumers
   *  that unmount normally get a fresh hook state for free. */
  reset: () => void;
}

/** @param testId resolved control test id — the button carries `${testId}-toggle`
 *  (pinned by the t1 forgot-reset-password spec). */
export function usePasswordReveal(
  testId: string,
  { disabled = false }: UsePasswordRevealOptions = {}
): UsePasswordRevealReturn {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const reset = useCallback(() => setVisible(false), []);
  return {
    type: visible ? 'text' : 'password',
    reset,
    toggle: (
      // Kit Button (pays down the pre-GR1 "migrate to ui primitive" marker the
      // raw <button> carried): icon-xs = 24px hit target (icon-only floor),
      // token focus ring, ghost hover, disabled path. 24px at end-1 sits
      // inside the control's 40px `pe-10` reserve — all rem, holds at
      // 85–150% root scale. type="button": inside forms, never a submitter.
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t('CommonText.hide_password') : t('CommonText.show_password')}
        className="absolute end-1 top-1/2 z-10 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        data-testid={`${testId}-toggle`}
      >
        {visible ? (
          <EyeClosedLinear className="size-4" aria-hidden="true" />
        ) : (
          <EyeLinear className="size-4" aria-hidden="true" />
        )}
      </Button>
    ),
  };
}
